// src/App.jsx
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthBootstrapStatus, useAuthToken, useSessionExpired, useUserData } from "./stores/useAuthStore";
import { useRootMachine } from "./utils/state-machine/MachineActors";
import AuthStoreHydrator from "./components/universal/AuthStoreHydrator/AuthStoreHydrator";
import BrandingHead from "./components/universal/BrandingHead";
import AppRoutes from "./routes/Routes";
import AnimatedText from "components/universal/AnimatedText/AnimatedText";
import SessionExpiredModal from "./components/universal/SessionExpiredModal/SessionExpiredModal";
import useRouteNamespacesReady from "./hooks/useRouteNamespacesReady";
import { isPublicRoute } from "./i18n";
import { SentryRouteContext } from "./observability/sentry";
import {
  usePostLoginBrandingStatus,
  usePreLoginBrandingStatus,
} from "./stores/useBrandingStore";
import { shouldWaitForBranding } from "./utils/branding/brandingBootstrap";

function AppLoadingFallback({ messageKey, defaultValue }) {
  const { t } = useTranslation("common", { useSuspense: false });

  return (
    <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center", gap: "10px" }}>
      <AnimatedText
        text={`${t(messageKey, { defaultValue })}...`}
        animationType="wave"
        size="large"
      />
    </div>
  );
}

function AppContent() {
  // Auth
  const authBootstrapStatus = useAuthBootstrapStatus();
  const authToken = useAuthToken();
  const sessionExpired = useSessionExpired();
  const userData = useUserData();
  const preLoginBrandingStatus = usePreLoginBrandingStatus();
  const postLoginBrandingStatus = usePostLoginBrandingStatus();

  // State machine
  const rootMachine = useRootMachine();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();


  useEffect(() => {
    if (userData) {
      rootMachine?.send({
        type: "authenticate",
        userData,
        navigateContext: navigate,
        locationContext: location,
        searchParamsContext: { searchParams, setSearchParams },
      });
    }
  }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPolicyPage = location.pathname === "/terms-and-conditions";
  const isPublicPage = isPublicRoute(location.pathname);

  const hasStoredAuthenticatedSession =
    !!authToken ||
    !!localStorage.getItem("token") ||
    !!localStorage.getItem("initial_token");
  const requiresRouteNamespacePreload =
    !isPublicPage &&
    authBootstrapStatus !== "policy_pending" &&
    hasStoredAuthenticatedSession;
  const routeNamespacesReady = useRouteNamespacesReady(
    location.pathname,
    requiresRouteNamespacePreload
  );

  useEffect(() => {
    if (authBootstrapStatus === "policy_pending" && !isPolicyPage) {
      navigate("/terms-and-conditions", { replace: true });
    }
  }, [authBootstrapStatus, isPolicyPage, navigate]);

  if (!isPublicPage && sessionExpired) return <SessionExpiredModal />;

  // Show "Starting session..." only while we're still determining auth (idle/bootstrapping)
  // or while we have a token but no userData yet. Do not show when already unauthenticated
  // or when policy_pending (that status has its own redirect to /terms-and-conditions).
  const stillDetermining =
    authBootstrapStatus === "idle" ||
    authBootstrapStatus === "bootstrapping";
  const hasTokenButNoUser =
    authBootstrapStatus !== "unauthenticated" &&
    authBootstrapStatus !== "policy_pending" &&
    !userData &&
    !sessionExpired;

  if (!isPublicPage && (stillDetermining || hasTokenButNoUser || !routeNamespacesReady)) {
    return (
      <AppLoadingFallback
        messageKey="spinner.validating_session"
        defaultValue="Validating session"
      />
    );
  }

  const isPublicAuthBootstrapPending = isPublicPage && stillDetermining;
  const isBrandingBootstrapPending = shouldWaitForBranding(
    authBootstrapStatus,
    preLoginBrandingStatus,
    postLoginBrandingStatus
  );

  if (isPublicAuthBootstrapPending || isBrandingBootstrapPending) {
    return (
      <AppLoadingFallback
        messageKey="spinner.loading"
        defaultValue="Loading data"
      />
    );
  }

  return <AppRoutes />;
}

function App() {
  return (
    <Router>
      <SentryRouteContext />
      <AuthStoreHydrator>
        <BrandingHead />
        <AppContent />
      </AuthStoreHydrator>
    </Router>
  );
}

export default App;
